import React, { useMemo, useState } from 'react';
import { FileDown, X } from 'lucide-react';
import { RANGES, rangeFor, summarize, toCsv } from '../lib/songUsage';

const formatDate = (iso) => new Date(iso).toLocaleDateString('en-GB');

/**
 * CCLI song usage report: how often each song was sent live in a date range,
 * with a CSV download for the licence report.
 */
const SongUsagePanel = ({ usage, onClear, onClose, dm, t }) => {
  const [preset, setPreset] = useState('month');
  const rows = useMemo(() => summarize(usage, rangeFor(preset)), [usage, preset]);
  const total = rows.reduce((sum, r) => sum + r.count, 0);

  const download = () => {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `song-usage-${preset}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clear = () => {
    if (usage.length > 0 && window.confirm(`Delete the whole song usage log (${usage.length} entries)? This cannot be undone.`)) {
      onClear();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-label="Song usage report">
      <div className={`w-full max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col rounded-2xl border p-5 shadow-2xl ${t.cardBg} ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-3 shrink-0">
          <h2 className={`text-lg font-bold ${t.textPrimary}`}>Song usage (CCLI)</h2>
          <button onClick={onClose} aria-label="Close" className={`p-1 rounded ${t.dropdownItem}`}><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 mb-3 shrink-0">
          <label htmlFor="usage-range" className={`text-xs font-bold ${t.textPrimary}`}>Period</label>
          <select
            id="usage-range"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className={`flex-1 p-2 text-sm rounded-lg border ${t.selectBg}`}
          >
            {RANGES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-slate-700/50">
          {rows.length === 0 ? (
            <p className={`p-4 text-sm ${t.textSecondary}`}>
              No songs were sent live in this period. Songs are logged automatically each time one goes live.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-xs ${t.textSecondary}`}>
                  <th className="p-2">Song</th>
                  <th className="p-2">CCLI #</th>
                  <th className="p-2 text-right">Used</th>
                  <th className="p-2 text-right">Last</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.title} className={`border-t border-slate-700/40 ${t.textPrimary}`}>
                    <td className="p-2">{r.title}</td>
                    <td className="p-2 font-mono text-xs">{r.ccli || '—'}</td>
                    <td className="p-2 text-right font-bold">{r.count}</td>
                    <td className="p-2 text-right text-xs">{formatDate(r.lastUsed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className={`text-xs mt-2 shrink-0 ${t.textMuted}`}>
          {rows.length} song{rows.length === 1 ? '' : 's'}, {total} use{total === 1 ? '' : 's'}. Sending the same song live again within 10 minutes counts once. Add a CCLI number when you add a song so it shows here.
        </p>

        <div className="flex gap-2 mt-3 shrink-0">
          <button
            onClick={download}
            disabled={rows.length === 0}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-bold bg-brand-600 text-slate-50 hover:bg-brand-500 disabled:opacity-40"
          >
            <FileDown className="w-4 h-4" /> Download CSV
          </button>
          <button
            onClick={clear}
            disabled={usage.length === 0}
            className="px-3 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"
          >
            Clear log
          </button>
        </div>
      </div>
    </div>
  );
};

export default SongUsagePanel;
